import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadIcon from "@mui/icons-material/Upload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useTenantTranslation } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { UploadedFileMeta } from "src/store/api/generatedApi";
import { UploadedFileType } from "components/file/upload/uploadedFileType";
import { getFileTypeDisplayName } from "./useRecentInventoryFiles";

export type FileUploadStatus =
  "pending" | "uploading" | "uploaded" | "processing" | "processed";

interface FileUploadCardProps {
  fileType: UploadedFileType;
  file: UploadedFileMeta | null;
  status: FileUploadStatus;
  onUpload?: () => void;
  showUploadButton?: boolean;
}

export const FileUploadCard: React.FC<FileUploadCardProps> = ({
  fileType,
  file,
  status,
  onUpload,
  showUploadButton = false,
}) => {
  const { t } = useTenantTranslation();
  const { toLocalDatetime } = useLocalDatetime();

  const getStatusColor = (): "default" | "primary" | "success" | "warning" => {
    switch (status) {
      case "processed":
        return "success";
      case "processing":
      case "uploading":
        return "primary";
      case "uploaded":
        return "primary";
      case "pending":
      default:
        return "default";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "processed":
        return <CheckCircleIcon fontSize="small" />;
      case "processing":
        return <CircularProgress size={16} />;
      case "uploading":
        return <CircularProgress size={16} />;
      case "uploaded":
        return <ScheduleIcon fontSize="small" />;
      case "pending":
      default:
        return null;
    }
  };

  const getStatusLabel = (): string => {
    switch (status) {
      case "processed":
        return t("processed");
      case "processing":
        return t("processing");
      case "uploading":
        return t("uploading");
      case "uploaded":
        return t("uploaded");
      case "pending":
      default:
        return t("pending");
    }
  };

  const displayName = getFileTypeDisplayName(fileType, t);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: status === "processed" ? "success.main" : "divider",
        borderWidth: status === "processed" ? 2 : 1,
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div">
              {displayName}
            </Typography>
            <Chip
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
              icon={getStatusIcon() ?? undefined}
            />
          </Box>

          {file ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>{t("fileName")}:</strong> {file.file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t("uploadedAt")}:</strong>{" "}
                {toLocalDatetime(file.uploaded_at, "YYYY-MM-DD HH:mm:ss")}
              </Typography>
            </Box>
          ) : null}

          {!file && (
            <Typography variant="body2" color="text.secondary">
              {t("noFileUploaded")}
            </Typography>
          )}

          {showUploadButton && onUpload ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                size="small"
                onClick={onUpload}
                color="primary"
                aria-label={`${t("upload")} ${displayName}`}
              >
                {file ? <RefreshIcon /> : <UploadIcon />}
              </IconButton>
            </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};
